import React, { useState } from 'react';
import { Modal, Button } from '../shared/Modal';
import { Field, TextInput, Select } from '../shared/FormFields';
import { adAccountLabel } from '../shared/adAccounts';

// Create / edit a workspace in one dialog. Replaces three separate interactions
// on the old page: the inline create form, the window.prompt rename, and the
// "Accounts" accordion that hid the per-workspace ad defaults. Edit mode saves
// the name and all three defaults in a single PATCH — /workspaces/:id already
// accepts them together.
//
// The DEFAULT workspace (row id 'account') edits through the same dialog with
// the same fields and the same frozen-once-saved rule — only the name is fixed
// (it is the account's name; PATCH /workspaces/account ignores it).
//
// Mount with a key (workspace id or 'new') so the form state resets per target.

const NONE = '— None —';

export default function WorkspaceFormModal({ mode = 'create', workspace, adAccounts, busy = false, onSubmit, onClose }) {
  const [name, setName] = useState(workspace?.name || '');
  const [website, setWebsite] = useState(workspace?.websiteUrl || '');
  const [google, setGoogle] = useState(workspace?.defaultGoogleAdsAccount || '');
  const [meta, setMeta] = useState(workspace?.defaultMetaAdsAccount || '');
  const [pixel, setPixel] = useState(workspace?.defaultMetaPixel || '');

  const googleAccounts = adAccounts?.google?.accounts || [];
  const metaAccounts = adAccounts?.facebook?.accounts || [];
  const pixels = metaAccounts.find((a) => a.id === meta)?.pixels || [];

  const creating = mode === 'create';
  const isDefault = !creating && workspace?.isDefault === true;
  const canSave = !busy && (isDefault || name.trim().length > 0);

  // Set-once: a default that is already saved can never be changed or cleared
  // (server: WORKSPACE_ADS_IMMUTABLE). Frozen fields render read-only — never
  // a Select, so a stale inventory missing the stored option can't post '' and
  // look like a clear — and submit passes the stored value straight through,
  // which the server treats as a no-op.
  const frozenGoogle = !creating && !!workspace?.defaultGoogleAdsAccount;
  const frozenMeta = !creating && !!workspace?.defaultMetaAdsAccount;
  const frozenPixel = !creating && !!workspace?.defaultMetaPixel;
  const frozenGoogleLabel = (() => {
    const a = googleAccounts.find((x) => x.id === workspace?.defaultGoogleAdsAccount);
    return a ? adAccountLabel('google', a) : (workspace?.defaultGoogleAdsAccount || '');
  })();
  const frozenMetaLabel = (() => {
    const a = metaAccounts.find((x) => x.id === workspace?.defaultMetaAdsAccount);
    return a ? adAccountLabel('meta', a) : (workspace?.defaultMetaAdsAccount || '');
  })();
  const frozenPixelLabel = (() => {
    const p = pixels.find((x) => x.id === workspace?.defaultMetaPixel);
    return p ? (p.name ? `${p.name} (${p.id})` : p.id) : (workspace?.defaultMetaPixel || '');
  })();
  const FROZEN_HINT = 'Locked when this workspace was first saved. It can no longer be changed.';

  const submit = () => {
    if (!canSave) return;
    onSubmit({
      name: name.trim(),
      website: website.trim(),
      google: frozenGoogle ? workspace.defaultGoogleAdsAccount : google,
      meta: frozenMeta ? workspace.defaultMetaAdsAccount : meta,
      pixel: frozenPixel ? workspace.defaultMetaPixel : pixel,
    });
  };

  return (
    <Modal
      title={creating ? 'New workspace' : isDefault ? 'Default workspace settings' : 'Workspace settings'}
      width={520}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSave}>
            {busy ? 'Saving…' : creating ? 'Create workspace' : 'Save changes'}
          </Button>
        </>
      )}
    >
      {creating && (
        <p style={{ margin: '0 0 16px', fontSize: '13px', lineHeight: 1.65, color: 'var(--text-secondary, #64748b)' }}>
          A workspace keeps a client&apos;s flows, data sources, knowledge and permanent store separate.
          Settings, integrations and credits stay shared across the account.
        </p>
      )}
      {isDefault && (
        <p style={{ margin: '0 0 16px', fontSize: '13px', lineHeight: 1.65, color: 'var(--text-secondary, #64748b)' }}>
          Everything not placed in another workspace lives here. These defaults apply to the agents,
          data sources and Analysis blocks in the default workspace.
        </p>
      )}

      {isDefault ? (
        <Field label="Workspace name" hint="The default workspace is named after your account.">
          <TextInput readOnly value={workspace?.name || ''} />
        </Field>
      ) : (
        <Field label="Workspace name">
          <TextInput value={name} onChange={setName} placeholder="Client or workspace name" maxLength={50} />
        </Field>
      )}

      <Field
        label="Website URL"
        info={isDefault
          ? "Your website. Agents use it when they need to read the site, and it resolves {{website_url}} in default-workspace runs."
          : "The client's website. Agents use it when they need to read the site, and it seeds this workspace's context."}
      >
        <TextInput value={website} onChange={setWebsite} placeholder="acme.com" maxLength={300} />
      </Field>

      {frozenGoogle ? (
        <Field label="Default Google Ads account" hint={FROZEN_HINT}>
          <TextInput readOnly value={frozenGoogleLabel} />
        </Field>
      ) : googleAccounts.length > 0 && (
        <Field
          label="Default Google Ads account"
          info="Every agent, data source and Analysis block in this workspace reads from this account. Once saved, this choice is permanent."
        >
          <Select
            value={google}
            onChange={setGoogle}
            options={[{ value: '', label: NONE }, ...googleAccounts.map((a) => ({ value: a.id, label: adAccountLabel('google', a) }))]}
          />
        </Field>
      )}

      {frozenMeta ? (
        <Field label="Default Meta ads account" hint={FROZEN_HINT}>
          <TextInput readOnly value={frozenMetaLabel} />
        </Field>
      ) : metaAccounts.length > 0 && (
        <Field
          label="Default Meta ads account"
          info="Every agent, data source and Analysis block in this workspace reads from this account. Once saved, this choice is permanent."
        >
          <Select
            value={meta}
            onChange={(v) => { setMeta(v); setPixel(''); }}
            options={[{ value: '', label: NONE }, ...metaAccounts.map((a) => ({ value: a.id, label: adAccountLabel('meta', a) }))]}
          />
        </Field>
      )}

      {frozenPixel ? (
        <Field label="Default Meta pixel" hint={FROZEN_HINT}>
          <TextInput readOnly value={frozenPixelLabel} />
        </Field>
      ) : pixels.length > 0 && (
        <Field label="Default Meta pixel">
          <Select
            value={pixel}
            onChange={setPixel}
            options={[{ value: '', label: NONE }, ...pixels.map((p) => ({ value: p.id, label: p.name ? `${p.name} (${p.id})` : p.id }))]}
          />
        </Field>
      )}
    </Modal>
  );
}
